import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Chip,
  Stack,
  Typography,
} from "@mui/joy";
import { diffLines } from "diff";
import { getExtension, getPreviewType } from "../common/htmlPreview";
import { useDownloadDocument, useGetDocumentDiffText } from "../services/api";

interface VersionDiffViewProps {
  workspaceId: string;
  currentVersionId: string;
  selectedVersionId: string;
  currentFilename: string;
  selectedFilename: string;
  currentLabel: string;
  selectedLabel: string;
  /** Set for SharePoint-backed documents: the version ids are SharePoint versions of this document. */
  sharePointDocId?: string;
}

interface DiffRow {
  kind: "added" | "removed" | "unchanged";
  leftLineNumber: number | null;
  rightLineNumber: number | null;
  prefix: "+" | "-" | " ";
  text: string;
}

interface LineDiffChange {
  value: string;
  added?: boolean;
  removed?: boolean;
}

const isTextDiffSupported = (filename: string): boolean => {
  const previewType = getPreviewType(filename);
  return (
    previewType === "text" ||
    previewType === "markdown" ||
    previewType === "html"
  );
};

// Word files are zip packages; the API extracts their text for the diff.
const isWordDiffSupported = (filename: string): boolean =>
  getExtension(filename) === "docx";

export const DIFF_UNAVAILABLE_MESSAGE =
  "Diff is only available for text, markdown, HTML, and Word (.docx) versions.";
export const MAX_DIFF_INPUT_LINES = 5_000;
export const MAX_DIFF_INPUT_CHARACTERS = 100_000;
export const MAX_RENDERED_DIFF_ROWS = 1_000;
export const DIFF_TOO_LARGE_MESSAGE =
  "This version is too large to compare here.";

const normalizeText = (value: string): string => value.replace(/\r\n/g, "\n");

const countLinesUpTo = (text: string, limit: number): number => {
  let lines = 1;
  for (let index = 0; index < text.length; index += 1) {
    if (text.charCodeAt(index) === 10) {
      lines += 1;
      if (lines > limit) {
        return lines;
      }
    }
  }
  return lines;
};

const buildDiffRows = (
  previousText: string,
  nextText: string,
): { rows: DiffRow[]; truncated: boolean } => {
  const rows: DiffRow[] = [];
  let truncated = false;
  const changes = diffLines(previousText, nextText, {
    newlineIsToken: true,
    ignoreWhitespace: false,
  }) as LineDiffChange[];
  let leftLine = 1;
  let rightLine = 1;

  for (const change of changes) {
    const rawLines = change.value.split("\n");
    const lines =
      rawLines[rawLines.length - 1] === "" ? rawLines.slice(0, -1) : rawLines;

    if (lines.length === 0) {
      continue;
    }

    for (const line of lines) {
      if (rows.length >= MAX_RENDERED_DIFF_ROWS) {
        truncated = true;
        break;
      }

      if (change.added) {
        rows.push({
          kind: "added",
          leftLineNumber: null,
          rightLineNumber: rightLine++,
          prefix: "+",
          text: line,
        });
        continue;
      }

      if (change.removed) {
        rows.push({
          kind: "removed",
          leftLineNumber: leftLine++,
          rightLineNumber: null,
          prefix: "-",
          text: line,
        });
        continue;
      }

      rows.push({
        kind: "unchanged",
        leftLineNumber: leftLine++,
        rightLineNumber: rightLine++,
        prefix: " ",
        text: line,
      });
    }

    if (truncated) {
      break;
    }
  }

  return { rows, truncated };
};

const VersionDiffView: React.FC<VersionDiffViewProps> = ({
  workspaceId,
  currentVersionId,
  selectedVersionId,
  currentFilename,
  selectedFilename,
  currentLabel,
  selectedLabel,
  sharePointDocId,
}) => {
  const downloadDocument = useDownloadDocument();
  const getDocumentDiffText = useGetDocumentDiffText();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailableReason, setUnavailableReason] = useState<string | null>(
    null,
  );
  const [currentText, setCurrentText] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const [isWordDiff, setIsWordDiff] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadDiff = async () => {
      setLoading(true);
      setError(null);
      setUnavailableReason(null);
      setCurrentText("");
      setSelectedText("");
      setIsWordDiff(false);

      const textDiff =
        isTextDiffSupported(currentFilename) &&
        isTextDiffSupported(selectedFilename);
      const wordDiff =
        isWordDiffSupported(currentFilename) &&
        isWordDiffSupported(selectedFilename);
      if (!textDiff && !wordDiff) {
        setUnavailableReason(DIFF_UNAVAILABLE_MESSAGE);
        setLoading(false);
        return;
      }

      try {
        if (wordDiff) {
          const fetchWordText = (versionId: string) =>
            sharePointDocId
              ? getDocumentDiffText(workspaceId, sharePointDocId, {
                  spVersionId: versionId,
                })
              : getDocumentDiffText(workspaceId, versionId);
          const [currentWord, selectedWord] = await Promise.all([
            fetchWordText(currentVersionId),
            fetchWordText(selectedVersionId),
          ]);
          if (cancelled) {
            return;
          }
          setIsWordDiff(true);
          setCurrentText(normalizeText(currentWord.text));
          setSelectedText(normalizeText(selectedWord.text));
          return;
        }

        const fetchVersion = (versionId: string, filename: string) =>
          sharePointDocId
            ? downloadDocument(workspaceId, sharePointDocId, filename, {
                responseType: "blob",
                spVersionId: versionId,
              })
            : downloadDocument(workspaceId, versionId, filename, {
                responseType: "blob",
              });
        const [currentBlob, selectedBlob] = await Promise.all([
          fetchVersion(currentVersionId, currentFilename),
          fetchVersion(selectedVersionId, selectedFilename),
        ]);

        if (!(currentBlob instanceof Blob) || !(selectedBlob instanceof Blob)) {
          throw new Error("Failed to load document content for diff");
        }

        const [currentContent, selectedContent] = await Promise.all([
          currentBlob.text(),
          selectedBlob.text(),
        ]);

        if (cancelled) {
          return;
        }

        setCurrentText(normalizeText(currentContent));
        setSelectedText(normalizeText(selectedContent));
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError((err as Error).message || "Failed to load version diff");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadDiff();

    return () => {
      cancelled = true;
    };
  }, [
    currentFilename,
    currentVersionId,
    downloadDocument,
    getDocumentDiffText,
    selectedFilename,
    selectedVersionId,
    sharePointDocId,
    workspaceId,
  ]);

  const inputLineCount = useMemo(
    () =>
      countLinesUpTo(currentText, MAX_DIFF_INPUT_LINES) +
      countLinesUpTo(selectedText, MAX_DIFF_INPUT_LINES),
    [currentText, selectedText],
  );
  const isTooLarge =
    inputLineCount > MAX_DIFF_INPUT_LINES ||
    currentText.length + selectedText.length > MAX_DIFF_INPUT_CHARACTERS;
  const { rows, truncated } = useMemo(
    () =>
      loading || isTooLarge
        ? { rows: [], truncated: false }
        : buildDiffRows(currentText, selectedText),
    [currentText, isTooLarge, loading, selectedText],
  );
  const hasChanges = useMemo(
    () => rows.some((row) => row.kind !== "unchanged"),
    [rows],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (error) {
    return <Alert color="danger">{error}</Alert>;
  }

  if (unavailableReason) {
    return <Alert color="neutral">{unavailableReason}</Alert>;
  }

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography level="body-xs" sx={{ color: "text.secondary" }}>
          {selectedLabel} vs {currentLabel}
        </Typography>
        {!isTooLarge && (
          <Chip
            size="sm"
            variant="soft"
            color={hasChanges ? "primary" : "neutral"}
          >
            {hasChanges ? "Changes found" : "No text changes"}
          </Chip>
        )}
      </Stack>

      {isWordDiff && (
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Comparing the text of the Word document. Formatting, images, headers
          and footers are not compared.
        </Typography>
      )}

      {isTooLarge ? (
        <Alert color="neutral">{DIFF_TOO_LARGE_MESSAGE}</Alert>
      ) : hasChanges ? (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              bgcolor: "background.surface",
            }}
          >
            {rows.map((row, index) => (
              <Box
                data-testid="version-diff-row"
                key={`${row.kind}-${index}-${row.leftLineNumber ?? "n"}-${row.rightLineNumber ?? "n"}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "44px 44px 16px minmax(0, 1fr)",
                  gap: 1,
                  px: 1.25,
                  py: 0.5,
                  alignItems: "start",
                  borderTop:
                    index === 0
                      ? "none"
                      : "1px solid var(--joy-palette-divider)",
                  bgcolor:
                    row.kind === "added"
                      ? "rgba(46, 125, 50, 0.10)"
                      : row.kind === "removed"
                        ? "rgba(211, 47, 47, 0.10)"
                        : "transparent",
                }}
              >
                <Typography
                  level="body-xs"
                  sx={{
                    color: "text.tertiary",
                    textAlign: "right",
                    fontFamily: "monospace",
                  }}
                >
                  {row.leftLineNumber ?? ""}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{
                    color: "text.tertiary",
                    textAlign: "right",
                    fontFamily: "monospace",
                  }}
                >
                  {row.rightLineNumber ?? ""}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{
                    color:
                      row.kind === "added"
                        ? "success.700"
                        : row.kind === "removed"
                          ? "danger.700"
                          : "text.tertiary",
                    fontFamily: "monospace",
                  }}
                >
                  {row.prefix}
                </Typography>
                <Typography
                  level="body-xs"
                  component="pre"
                  sx={{
                    m: 0,
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "text.primary",
                  }}
                >
                  {row.text || " "}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Alert color="neutral">
          No text changes relative to the current version.
        </Alert>
      )}
      {truncated && !isTooLarge && (
        <Typography level="body-xs" color="neutral">
          Showing first {MAX_RENDERED_DIFF_ROWS.toLocaleString()} changes.
        </Typography>
      )}
    </Stack>
  );
};

export default VersionDiffView;
